/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { AppButton } from 'uikit/buttons';
import {
  ApiException,
  FieldEditReason,
  FileParameter,
  FormLifecycleState,
  FormSkipReason,
  IFormDto,
  IFormResultDto,
  MarkFormResultAsSkippedDto,
  ProcessFormResultDto,
  SkipFormResultDto,
  UpdateFormResultDto,
} from '../../../services/api/api-client';
import { logger } from 'src/application/logging/logging';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from 'src/application/hooks/useModal';
import { TypographyStyles } from 'src/styles';
import { ReactComponent as CheckIcon } from 'assets/img/common/checked_16.svg';
import styles, { formModalContainerWithNavbar } from '../uiEditor/uiEditor.module.scss';
import { LocalizedResourceDictionaryKeys } from '../../../application/localisation/i18next';
import { useNotEditableFieldModal } from './notEditableFieldsModal/notEditableFieldsModal';
import { useContextSelector } from 'use-context-selector';
import clsx from 'clsx';
import { useScopedTranslation } from '../../../application/localisation/useScopedTranslation';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { CloseConfirmationModal } from './closeConfirmationModal';
import { useTagsModal } from './tagsModal/TagsModal.component';
import { FormFillContext } from '../uiEditor/provider/formFill.context';
import { FormModalComponentProps } from '../uiEditor/uiEditor';
import { findNodeIdsWithDataKey, isAppAttachmentValue, isValid, pageStacksIsEqual } from '../uiEditor/uiEditor-helper';
import { useReasonModal } from './reasonModal/reasonModal.component';
import { UiEditorComponent } from '../uiEditor/uiEditor.component';
import { AppButtonWithMenu } from 'uikit/buttons/buttonWithMenu/buttonWithMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { SkipFormState, useFormResultSkipping } from './skipFormResultModal/useFormResultSkipping';
import { ReactComponent as ThinCrossIcon } from '../../../assets/img/common/thin_cross_16.svg';
import { useTranslation } from 'react-i18next';
import {
  getNotificationBatchesQueryKey,
  getNotificationCountsQueryKey,
  getNotificationsQueryKey,
} from 'src/services/api/api-client/NotificationQuery';
import {
  getPatientDeviceBindingsQueryKey,
  getPatientQueryKey,
  getPatientsQueryKey,
  getPatientStudyCountersQueryKey,
} from '../../../services/api/api-client/PatientQuery';
import { OverviewField } from 'uikit/fields/overview/OverviewField';
import { Loading } from 'uikit/suspense/Loading';
import { getPatientRouteProgressQueryKey } from '../../../services/api/api-client/StudyRoutesQuery';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { FormFillNavigationContext } from '../uiEditor/formFillingNavigation/FormFillingNavigationContext';
import { useGetNavigationTree } from '../uiEditor/formFillingNavigation/formFillingNavigationBar/useGetNavigationTree';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useGetFieldChanges } from './tagsModal/useGetFieldChanges';
import {
  patchResults,
  processResults,
  patchAndSubmitForm,
  submitForm,
  markFormResultAsSkipped,
  skipFormResult,
} from 'src/services/api/api-client/FormsClient';
import { FormTab } from 'src/features/forms/overview/useFormOverview';
import { HistoryTab } from 'src/features/forms/overview/historyTab/HistoryTab';
import {
  getFormHistoryByIdQueryKey,
  getFormResultByIdQueryKey,
  getFormResultsQueryKey,
} from 'src/services/api/api-client/FormsQuery';
import { conditionalConcat } from 'src/helpers/arrayHelpers';

export const FormModalComponent: FC<FormModalComponentProps> = (props) => {
  const { id: studyId, hasEcrf, groups, tagFilters } = useStudy() ?? {};
  const client = useQueryClient();
  const { t } = useScopedTranslation('Forms');
  const commonLocalizer = useCommonLocalization();

  const confirmationModal = useModal();
  const reasonModal = useReasonModal();
  const notEditableFieldsModal = useNotEditableFieldModal();
  const tagsModal = useTagsModal(tagFilters ?? null);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [commonError, setCommonError] = useState<string | undefined>();

  const {
    mode,
    isSubmitting,
    formConfig,
    formResultVersion,
    values: dataBlockValues,
    notEditableNodes,
    dataBlockValidation,
    skippedFieldsData,
    pageStacks,
    currentPageStack,
    patient,
    setIsSubmitting,
    resetState,
    goToNextPage,
    goToPrevPage,
    navigateToBlock,
    triggerValidation,
    setDataBlockValidation,
    formTab,
  } = useContextSelector(FormFillContext, (x) => x);

  const skipFormHandler = useCallback(
    async (formState: SkipFormState) => {
      if (!formConfig) throw new Error('No form config is provided');
      if (!props.patientId) throw new Error('No patient id is provided');

      let formResult: IFormResultDto;
      try {
        if (!formResultVersion?.formResultId) {
          const dto = new SkipFormResultDto({
            patientId: props.patientId,
            formConfigId: formConfig.id,
            stepName: props.stepName ?? null,
            skipReason: formState.skipReason!,
            skipReasonDetails: formState.skipReasonDetails,
          });

          formResult = await skipFormResult(dto);
        } else {
          const dto = new MarkFormResultAsSkippedDto({
            skipReason: formState.skipReason!,
            skipReasonDetails: formState.skipReasonDetails,
          });

          formResult = await markFormResultAsSkipped(formResultVersion.formResultId, dto);
        }
      } catch (e: any) {
        return e.message;
      }

      await Promise.all([
        client.invalidateQueries(getFormResultsQueryKey(formResult.formConfigId)),
        client.invalidateQueries(getFormResultByIdQueryKey(formResult.id)),
        client.invalidateQueries(getPatientRouteProgressQueryKey(formResult.patientId)),
        client.invalidateQueries(getPatientQueryKey(formResult.patientId)),
        client.invalidateQueries(getPatientsQueryKey()),
        client.invalidateQueries(getNotificationCountsQueryKey(studyId)),
        client.invalidateQueries(getNotificationBatchesQueryKey(undefined, [studyId!])),
        client.invalidateQueries(getNotificationsQueryKey(undefined, studyId)),
        client.invalidateQueries(getFormHistoryByIdQueryKey(formResult.id)),
      ]);
    },
    [client, formConfig, formResultVersion?.formResultId, props.patientId, props.stepName, studyId],
  );

  const formResultSkipping = useFormResultSkipping({
    title: formConfig!.type,
    subTitle: props.stepName,
    text: t('FormResultSkipping.Modal.Description'),
    callback: skipFormHandler,
  });

  const { navBarIsOpen, setNavBarIsOpen } = useContextSelector(FormFillNavigationContext, (x) => x);
  const navigationTree = useGetNavigationTree();

  // This effect clear common error when any validation changes
  useEffect(() => {
    setCommonError(undefined);
  }, [dataBlockValidation]);

  const { anyChanges, anyEditedFields } = useGetFieldChanges();

  const checkTouchedFields = useCallback(() => {
    if (anyChanges && mode !== 'BuildingPreview') {
      return confirmationModal.openModal();
    }

    props.modal.onHide();
  }, [anyChanges, confirmationModal, mode, props.modal]);

  const getPatientTags: () => Promise<Record<string, string> | null> = useCallback(async () => {
    const doctorHasMultiValuedFilters =
      tagFilters && Object.entries(tagFilters).some(([key, values]) => Array.isArray(values) && values.length > 1);

    const tags =
      formConfig?.isPatientCreationForm && mode === 'Create' && doctorHasMultiValuedFilters
        ? await tagsModal.open()
        : Object.entries(tagFilters || {}).reduce((acc: Record<string, string>, curr: [string, string[]]) => {
            const [key, values] = curr;
            if (Array.isArray(values) && values.length === 1) {
              acc[key] = values[0];
            }

            return acc;
          }, {} as Record<string, string>);

    return tags;
  }, [formConfig?.isPatientCreationForm, mode, tagFilters, tagsModal]);

  const processResult = useCallback(
    async (formConfigId: number, asSubmitted: boolean, fieldEditReasons?: { [key: string]: FieldEditReason }) => {
      const isRecalled = formResultVersion?.lifecycleState === FormLifecycleState.ApproveRecalled;

      const files: FileParameter[] = Object.entries(dataBlockValues ?? {}).reduce(
        (arr: FileParameter[], [dataKey, value]) =>
          Array.isArray(value) && value.every(isAppAttachmentValue)
            ? [...arr, ...value.flatMap((v) => ({ data: v.file, fileName: v.id! })).filter((x) => !!x?.data?.size)]
            : arr,
        [],
      );

      if (!formResultVersion?.formResultId) {
        const patientTags = await getPatientTags();
        return processResults(
          true,
          files,
          new ProcessFormResultDto({
            studyId: studyId!,
            patientId: props.patientId!,
            formId: formConfigId,
            values: JSON.stringify(dataBlockValues),
            stepName: props.stepName,
            tags: patientTags!,
            fieldSkipReasons: skippedFieldsData || {},
            asSubmitted: asSubmitted,
          }),
        );
      }

      if (asSubmitted) {
        return anyChanges || isRecalled
          ? patchAndSubmitForm(
              true,
              files,
              new UpdateFormResultDto({
                studyId: studyId!,
                patientId: props.patientId!,
                formResultId: formResultVersion?.formResultId,
                values: JSON.stringify(dataBlockValues),
                fieldSkipReasons: skippedFieldsData || {},
                fieldEditReasons: fieldEditReasons || {},
              }),
            )
          : submitForm(formResultVersion?.formResultId);
      }

      return patchResults(
        true,
        files,
        new UpdateFormResultDto({
          studyId: studyId!,
          patientId: props.patientId!,
          formResultId: formResultVersion?.formResultId,
          values: JSON.stringify(dataBlockValues),
          fieldSkipReasons: skippedFieldsData || {},
          fieldEditReasons: fieldEditReasons || {},
        }),
      );
    },
    [
      anyChanges,
      dataBlockValues,
      formResultVersion,
      getPatientTags,
      props.patientId,
      props.stepName,
      skippedFieldsData,
      studyId,
    ],
  );

  const sendResult = useCallback(
    async (asSubmitted: boolean, fieldEditReasons?: { [key: string]: FieldEditReason }) => {
      if (!formConfig) throw new Error('formConfig is not provided');

      setIsSubmitting?.(true);

      try {
        const newFormResult = await processResult(formConfig.id, asSubmitted, fieldEditReasons);

        await Promise.all(
          conditionalConcat(
            [
              client.invalidateQueries(getPatientRouteProgressQueryKey(props.patientId!)),
              client.invalidateQueries(getPatientDeviceBindingsQueryKey(props.patientId!)),
              client.invalidateQueries(getPatientStudyCountersQueryKey(studyId!)),
              client.invalidateQueries(getPatientsQueryKey()),
              client.invalidateQueries(getNotificationCountsQueryKey(studyId!)),
              client.invalidateQueries(getNotificationBatchesQueryKey(undefined, [studyId!])),
              client.invalidateQueries(getNotificationsQueryKey(undefined, studyId)),
            ],
            !!formResultVersion?.formResultId && [
              client.invalidateQueries(getFormHistoryByIdQueryKey(formResultVersion.formResultId)),
            ],
          ),
        );

        // For submitted animation
        setIsSubmitted(true);
        setTimeout(async () => {
          await props.onSubmitted?.(newFormResult);

          props.modal.onHide();

          setIsSubmitted(false);
          setIsSubmitting?.(false);
        }, 1700);
      } catch (error: any) {
        setIsSubmitting?.(false);

        const isApiException = ApiException.isApiException(error);
        if (isApiException) {
          logger().error(error.message, error);
          setCommonError(t('CommonError'));
          return;
        }

        if (!error.errors || (error.errors && !Object.keys(error.errors).length)) {
          logger().error(t('CommonError'), error);
          setCommonError(t('CommonError'));
          return;
        }

        for (const dataKey of Object.keys(error.errors)) {
          const nodeIds = findNodeIdsWithDataKey(formConfig.layoutSchema, dataKey);
          if (!nodeIds.length) {
            setCommonError(t('CommonError'));
            continue;
          }

          for (const nodeId of nodeIds) {
            if (!dataBlockValidation || !Object.keys(dataBlockValidation!).includes(nodeId)) continue;

            setDataBlockValidation?.({
              [nodeId]: {
                validationState: 'Fail',
                text: commonLocalizer(
                  `ServerErrors.${error.errors[dataKey][0]}` as LocalizedResourceDictionaryKeys,
                ) as string,
              },
            });
          }

          const firstErrorBlock = _.first(nodeIds);
          navigateToBlock?.(firstErrorBlock);
        }
      }
    },
    [
      formConfig,
      setIsSubmitting,
      processResult,
      client,
      props,
      studyId,
      formResultVersion,
      t,
      navigateToBlock,
      dataBlockValidation,
      setDataBlockValidation,
      commonLocalizer,
    ],
  );

  const onSkipClick = useCallback(async () => {
    if (!formConfig) throw new Error('formConfig is not provided');

    const skippingResult = await formResultSkipping.start();
    if (skippingResult.$type === 'Canceled') return;

    props.modal.onHide();
  }, [formConfig, formResultSkipping, props.modal]);

  const saveHandler = useCallback(
    ({ asSubmitted }: { asSubmitted: boolean }) =>
      async () => {
        confirmationModal.closeModal();
        setCommonError(undefined);

        const validationResult = triggerValidation?.({ ignoreIsRequiredRule: hasEcrf && !asSubmitted, eCRF: hasEcrf });
        if (validationResult && !isValid(validationResult)) {
          const firstFailedBlock = _.first(
            Object.keys(validationResult).filter((key) => validationResult[key].validationState === 'Fail'),
          );
          navigateToBlock?.(firstFailedBlock);
          return;
        }

        const reasonModalResult = mode === 'Edit' && anyEditedFields ? await reasonModal.open() : null;
        if (notEditableNodes!.length > 0) {
          await notEditableFieldsModal.open();
        }

        await sendResult(asSubmitted, reasonModalResult?.fieldEditReasons);
      },
    [
      confirmationModal,
      triggerValidation,
      hasEcrf,
      mode,
      reasonModal,
      notEditableNodes,
      sendResult,
      navigateToBlock,
      notEditableFieldsModal,
      anyEditedFields,
    ],
  );

  const rightButton = useMemo(() => {
    const isLastPage = pageStacksIsEqual(_.last(pageStacks), currentPageStack);
    if (!isLastPage && !isSubmitting && !confirmationModal.visible)
      return (
        <AppButton
          text={commonLocalizer('Common_Next')}
          variant={'button'}
          colorSchema={'primary'}
          onClick={goToNextPage}
          disabled={isSubmitting || isSubmitted}
          isLoading={isSubmitting}
          hasLoaded={isSubmitted}
        />
      );

    const isNoteForm = groups?.find((x) => x.id === patient?.group)?.formConfigIdForNotes === formConfig?.id;

    if (hasEcrf && !isNoteForm) {
      if (props.editAsSubmitted) {
        return (
          <AppButton
            testId={'submit-button'}
            text={t('FillInModal.Submit')}
            variant={'button'}
            colorSchema={'primary'}
            onClick={saveHandler({ asSubmitted: true })}
            disabled={isSubmitting || isSubmitted}
            isLoading={isSubmitting}
            hasLoaded={isSubmitted}
          />
        );
      } else {
        const options = [
          {
            key: 0,
            icon: <CheckIcon />,
            text: t('FillInModal.SaveAndSubmit'),
            action: saveHandler({ asSubmitted: true }),
          },
        ] as DropDownMenuOption[];

        if (formConfig?.canBeSkipped)
          options.push({
            key: 1,
            icon: <ThinCrossIcon />,
            text: t('FillInModal.Skip'),
            action: onSkipClick,
          });

        return (
          <AppButtonWithMenu
            testIdPrefix={'save-as'}
            options={options}
            buttonText={t('FillInModal.SaveAsDraft')}
            onClick={saveHandler({ asSubmitted: false })}
            disabled={isSubmitting || isSubmitted}
            isLoading={isSubmitting}
            hasLoaded={isSubmitted}
            mainButtonDisabled={!anyChanges}
          />
        );
      }
    }

    return (
      <AppButton
        text={t('Controls.SaveButton')}
        variant={'button'}
        colorSchema={'primary'}
        onClick={saveHandler({ asSubmitted: false })}
        disabled={isSubmitting || isSubmitted || !anyChanges}
        isLoading={isSubmitting}
        hasLoaded={isSubmitted}
      />
    );
  }, [
    anyChanges,
    commonLocalizer,
    confirmationModal.visible,
    currentPageStack,
    formConfig?.canBeSkipped,
    formConfig?.id,
    goToNextPage,
    groups,
    hasEcrf,
    isSubmitted,
    isSubmitting,
    pageStacks,
    patient?.group,
    props.editAsSubmitted,
    saveHandler,
    onSkipClick,
    t,
  ]);

  const footer = useMemo(() => {
    if (formTab !== FormTab.FormOverview && formTab) return undefined;

    return {
      errors: commonError,
      legendSection: !formConfig?.legend?.length ? undefined : <LegendBlock formConfig={formConfig} />,
      customButtonBlock: props.footer,
      navigationButton:
        !navigationTree.length || formResultVersion?.isSkipped
          ? undefined
          : {
              text: navBarIsOpen ? t('FillInModal.HideNavBar') : t('FillInModal.ShowNavBar'),
              onClick: () => setNavBarIsOpen?.((x) => !x),
              iconFirst: !navBarIsOpen,
              className: styles.openNavBarButton,
            },
      leftButton:
        mode === 'Overview' || mode === 'BuildingPreview'
          ? undefined
          : pageStacksIsEqual(_.first(pageStacks), currentPageStack)
          ? {
              text: commonLocalizer('Common_Cancel'),
              onClick: checkTouchedFields,
              disabled: isSubmitting || isSubmitted,
            }
          : {
              text: commonLocalizer('Common_Back'),
              onClick: goToPrevPage,
              disabled: isSubmitting || isSubmitted,
            },
      customRightButton: mode === 'Overview' || mode === 'BuildingPreview' ? undefined : rightButton,
    };
  }, [
    formTab,
    commonError,
    props.footer,
    formConfig,
    navigationTree.length,
    formResultVersion?.isSkipped,
    navBarIsOpen,
    t,
    mode,
    pageStacks,
    currentPageStack,
    commonLocalizer,
    checkTouchedFields,
    isSubmitting,
    isSubmitted,
    goToPrevPage,
    rightButton,
    setNavBarIsOpen,
  ]);

  return (
    <>
      <DialogModal
        {...props.modal}
        fullHeight
        footer={footer}
        onHide={() => !isSubmitting && !isSubmitted && checkTouchedFields()}
        onDismissed={resetState}
        isDisabled={isSubmitting || isSubmitted}
        containerClassName={clsx(styles.formModalContainer, {
          [formModalContainerWithNavbar]: navBarIsOpen,
          [styles.overviewModalContainer]: mode === 'Overview',
        })}
        bodyClassName={styles.formModalBody}
      >
        <Loading loading={!!props.isLoading} containerClassName={styles.loader} testId={'form-modal'}>
          {props.navigationMenu}
          {formResultVersion?.isSkipped && mode === 'Overview' && formTab === FormTab.FormOverview ? (
            <SkippedFormBody
              skipReason={formResultVersion.skipReason!}
              skipReasonDetails={formResultVersion.skipReasonDetails!}
            />
          ) : formTab === FormTab.HistoryOfChanges ? (
            <div className={styles.data}>
              <HistoryTab formResultId={formResultVersion!.formResultId} hasEcrf={hasEcrf} />
            </div>
          ) : (
            <>
              {props.formScoreComponent}
              <div className={styles.data}>
                <UiEditorComponent isInDesignerMode={false} layoutScheme={formConfig?.layoutSchema} />
              </div>
            </>
          )}
        </Loading>
      </DialogModal>

      {tagsModal.element}
      {reasonModal.element}
      {notEditableFieldsModal.element}
      {formResultSkipping.modal}
      <CloseConfirmationModal modal={confirmationModal} saveButton={rightButton} onDiscard={props.modal.onHide} />
    </>
  );
};

const LegendBlock: FC<{ formConfig: IFormDto | undefined }> = ({ formConfig }) => {
  return (
    <div className={clsx(TypographyStyles.plainText12, styles.legendContainer)}>
      {formConfig?.legend?.map((text) => (
        <div key={text}>{text}</div>
      ))}
    </div>
  );
};

const SkippedFormBody: FC<{ skipReason: FormSkipReason; skipReasonDetails: string }> = ({
  skipReason,
  skipReasonDetails,
}) => {
  const { t } = useTranslation();

  const translatedSkipReason =
    skipReason === FormSkipReason.NotApplicable
      ? t('Forms.FormSkipReason.NotApplicable')
      : skipReason === FormSkipReason.Missed
      ? t('Forms.FormSkipReason.Missed')
      : undefined;

  return (
    <div>
      <OverviewField label={t('Forms.Overview.SkippedFormBody.SkipReason')} value={translatedSkipReason} />
      <OverviewField label={t('Forms.Overview.SkippedFormBody.SkipReasonDetails')} value={skipReasonDetails} />
    </div>
  );
};
